import { MONACO_BASE_OPTIONS, MONACO_VALIDATION_OPTIONS } from "../constants";

/**
 * Get the complete Monaco editor options
 * @param {boolean} isDarkMode - Whether dark mode is enabled
 * @returns {Object} Monaco editor options
 */
export const getMonacoOptions = (isDarkMode) => ({
  ...MONACO_BASE_OPTIONS,
  ...MONACO_VALIDATION_OPTIONS,
});

/**
 * Configure Monaco editor before mount to disable validation
 * @param {Object} monaco - The Monaco instance
 */
export const configureMonacoBeforeMount = (monaco) => {
  try {
    // Disable validation for TypeScript and JavaScript
    if (
      monaco.languages.typescript?.javascriptDefaults?.setDiagnosticsOptions
    ) {
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: true,
      });
    }

    if (
      monaco.languages.typescript?.typescriptDefaults?.setDiagnosticsOptions
    ) {
      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: true,
      });
    }

    // Disable validation for JSON
    if (monaco.languages.json?.jsonDefaults?.setDiagnosticsOptions) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: false,
      });
    }

    // Disable validation for CSS/SCSS/LESS
    if (monaco.languages.css?.cssDefaults?.setDiagnosticsOptions) {
      monaco.languages.css.cssDefaults.setDiagnosticsOptions({
        validate: false,
      });
    }

    if (monaco.languages.scss?.scssDefaults?.setDiagnosticsOptions) {
      monaco.languages.scss.scssDefaults.setDiagnosticsOptions({
        validate: false,
      });
    }

    if (monaco.languages.less?.lessDefaults?.setDiagnosticsOptions) {
      monaco.languages.less.lessDefaults.setDiagnosticsOptions({
        validate: false,
      });
    }

    // Disable validation for HTML
    if (monaco.languages.html?.htmlDefaults?.setOptions) {
      monaco.languages.html.htmlDefaults.setOptions({
        validate: false,
      });
    }
  } catch (error) {
    console.warn("[Monaco Config] Error disabling validation:", error);
    // Continue anyway - validation disabling is not critical
  }
};

/**
 * Get the Monaco theme based on dark mode
 * @param {boolean} isDarkMode - Whether dark mode is enabled
 * @returns {string} The Monaco theme name
 */
export const getMonacoTheme = (isDarkMode) => {
  return isDarkMode ? "vs-dark" : "light";
};

/**
 * Create custom Monaco keybindings
 * @param {Object} monaco - The Monaco instance
 * @param {Object} editor - The editor instance
 * @param {Function} onSave - Callback for save action
 */
export const setupMonacoKeybindings = (monaco, editor, onSave) => {
  // Cmd/Ctrl+S for save
  editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
    if (onSave) onSave();
  });
};

/**
 * Configure Monaco editor after mount
 * @param {Object} editor - The editor instance
 * @param {Object} monaco - The Monaco instance
 */
export const configureMonacoAfterMount = (editor, monaco) => {
  // Focus the editor
  editor.focus();

  // Set up any additional configurations
  editor.updateOptions({
    // Ensure smooth cursor animation
    cursorBlinking: "smooth",
    cursorSmoothCaretAnimation: true,

    // Better selection
    selectionHighlight: true,
    occurrencesHighlight: true,

    // Better scrolling
    smoothScrolling: true,

    // Format on paste
    formatOnPaste: false, // Disabled to avoid unexpected changes

    // Bracket matching
    matchBrackets: "always",

    // Folding
    folding: true,
    foldingStrategy: "indentation",
    showFoldingControls: "mouseover",

    // Rendering
    renderWhitespace: "selection",
    renderControlCharacters: false,
    renderIndentGuides: true,
    renderLineHighlight: "all",

    // Accessibility
    accessibilitySupport: "auto",
  });
};

/**
 * Get language-specific editor options
 * @param {string} language - The language identifier
 * @returns {Object} Language-specific options
 */
export const getLanguageSpecificOptions = (language) => {
  const options = {};

  switch (language) {
    case "python":
      options.tabSize = 4;
      options.insertSpaces = true;
      break;
    case "makefile":
      options.insertSpaces = false; // Makefiles require tabs
      break;
    case "yaml":
    case "yml":
      options.tabSize = 2;
      options.insertSpaces = true;
      break;
    case "javascript":
    case "typescript":
    case "json":
    case "html":
    case "css":
      options.tabSize = 2;
      options.insertSpaces = true;
      break;
    default:
      options.tabSize = 2;
      options.insertSpaces = true;
  }

  return options;
};

/**
 * Register custom Monaco themes if needed
 * @param {Object} monaco - The Monaco instance
 */
export const registerCustomThemes = (monaco) => {
  // You can define custom themes here if needed
  // For now, we use the default vs-dark and light themes
};

/**
 * Dispose Monaco editor properly
 * @param {Object} editor - The editor instance
 */
export const disposeMonacoEditor = (editor) => {
  if (editor && typeof editor.dispose === "function") {
    editor.dispose();
  }
};
